import { useMediaQuery } from '@abyss/web/hooks/useMediaQuery';
import { IconMaterial } from '@abyss/web/ui/IconMaterial';
import { Text } from '@abyss/web/ui/Text';
import { Tooltip } from '@abyss/web/ui/Tooltip';
import { ChipState } from '#/store/useChipStore/chipStore';
import { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { useShallow } from 'zustand/react/shallow';

import { PortalContext } from '../../context/PortalContext';
import { useSmartChoice } from '../../hooks/useSmartChoice';
import { useSnackcardViewAllCount } from '../../hooks/useSnackcardViewAllCount';
import { useChipStore } from '../../store/useChipStore';
import { CareCategories } from '../Constants';
import {
  carouselMinScreen,
  carouselScreen,
  phoneOnly,
} from '../ConstantsStyles';
import { LinkWithTracking } from '../LinkWithTracking/LinkWithTracking';
import { PortalName } from '../Portal';
import { SnackDetails, SnackInfoCard } from '../SnackCard/SnackCard.styles';

type SnackCardViewAllLinkProps = {
  heading?: string;
  handleViewAllOnClick?: () => void;
  providerType?: string;
  isVirtualCareOffered?: boolean;
  hasPhysicalAddress?: boolean;
  isOutOfCntyIndicatorEnabled?: boolean;
  isCaraousel?: boolean;
  isVirtualVisit?: boolean;
  maxBadgeCount?: number;
  isHeadingAcronym?: boolean;
};

export const getEllipsisText = (text: string) =>
  text?.length > 35 ? `${text.slice(0, 35)}...` : text;

export const SnackCardViewAllLink = ({
  heading,
  handleViewAllOnClick,
  providerType,
  isVirtualCareOffered,
  hasPhysicalAddress,
  isOutOfCntyIndicatorEnabled = false,
  isCaraousel = false,
  isVirtualVisit = false,
  maxBadgeCount = 0,
  isHeadingAcronym = false,
}: SnackCardViewAllLinkProps) => {
  const { t } = useTranslation();
  const viewAllHeading = isHeadingAcronym ? heading : heading?.toLowerCase();
  const chipStore = useChipStore(useShallow((state: ChipState) => state));
  const { chipValue } = chipStore;

  const infoCardHeight = (isSmartChoiceFFEnabled, maxBadgeCount = 0) => {
    const cardHeights = {
      4: { height: '320px', '@screen < $sm': { height: '295px' } },
      3: { height: '290px', '@screen < $sm': { height: '270px' } },
      2: { height: '260px', '@screen < $sm': { height: '245px' } },
      1: { height: '230px', '@screen < $sm': { height: '220px' } },
      0: { height: '230px', '@screen < $sm': { height: '220px' } },
    };

    if (isSmartChoiceFFEnabled) {
      return { minHeight: '222px', '@screen < $sm': { minHeight: '200px' } };
    }
    return cardHeights[maxBadgeCount];
  };

  const mobileScreen = useMediaQuery(phoneOnly);
  const carouselBreakPoint = useMediaQuery(carouselScreen);
  const carouselMinBreakPoint = useMediaQuery(carouselMinScreen);

  const { isExpandViewAllEnabled } = useSnackcardViewAllCount();
  const smartChoiceEligibility = useSmartChoice();
  const isSmartChoiceFFEnabled = smartChoiceEligibility.isAvailable;
  const { portalData } = useContext(PortalContext);

  const isUmr = portalData?.portalName === PortalName.UMR;
  const getHeight = () => {
    let css = {};
    const displayVirtualProviderGroup =
      isVirtualCareOffered &&
      !hasPhysicalAddress &&
      providerType === 'Medical Group';

    if (isSmartChoiceFFEnabled) {
      css = { maxHeight: mobileScreen ? '200px' : '270px' };
    }

    if (displayVirtualProviderGroup) {
      css = { maxHeight: '201px' };
    }

    if (carouselBreakPoint && isOutOfCntyIndicatorEnabled) {
      css = { marginBottom: '25px' };
    }
    if (
      (carouselBreakPoint && isOutOfCntyIndicatorEnabled && isCaraousel) ||
      (mobileScreen && isOutOfCntyIndicatorEnabled)
    ) {
      css = { marginBottom: '34px' };
    }
    if (carouselBreakPoint && !isOutOfCntyIndicatorEnabled && !isCaraousel) {
      css = { marginBottom: '2px' };
    }

    if (carouselMinBreakPoint && isOutOfCntyIndicatorEnabled) {
      css = { marginBottom: '26px' };
    }

    if (carouselMinBreakPoint && isVirtualVisit) {
      css = { height: '200px' };
    }

    if (
      isUmr &&
      isSmartChoiceFFEnabled &&
      (chipValue === CareCategories.PRIMARY_CARE ||
        chipValue === CareCategories.MEDICAL_SPECIALISTS) &&
      providerType !== 'Medical Group'
    ) {
      css = { height: mobileScreen ? '200px' : '280px' };
    }

    css = {
      ...css,
      ...infoCardHeight(isSmartChoiceFFEnabled, maxBadgeCount),
    };

    return css;
  };

  return (
    <SnackInfoCard
      alignContent="center"
      alignItems="center"
      css={{
        ...{
          justifyContent: 'center',
          width: 'min-content',
          marginBottom: '2px',
        },
        ...getHeight(),
      }}
      data-auto-testid={`snack-card-${heading}`.replace(/ /g, '-')}
      data-testid={`snack-card-${heading}`.replace(/ /g, '-')}
      direction="column"
    >
      <SnackDetails
        alignContent="center"
        alignItems="center"
        direction="column"
      >
        <LinkWithTracking
          after={
            <IconMaterial
              icon="chevron_right"
              size={(isExpandViewAllEnabled && '$md') || '$xs'}
            />
          }
          analyticsInfo={{
            location: `body:${heading} results`,
          }}
          css={{
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-start',
            width: mobileScreen ? '138px' : '162px',
            '&:hover': { borderColor: 'transparent' },
            '& .abyss-insert-element-root': {
              justifyContent: 'flex-start',
            },
          }}
          data-auto-testid={`cta-snackcard-${heading}`}
          data-testid={`cta-snackcard-${heading}`}
          isStandardAnchor
          onClick={handleViewAllOnClick}
        >
          <Tooltip
            align="center"
            asChild
            content={`${t('All')} ${viewAllHeading}`}
            position="top"
          >
            <Text
              color="$interactive1"
              css={{
                textAlign: 'left',
                textOverflow: 'ellipsis',
                overflow: 'hidden',
                wordWrap: 'break-word',
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
              }}
              fontWeight="$bold"
            >
              {getEllipsisText(`${t('All')} ${viewAllHeading}`)}
            </Text>
          </Tooltip>
        </LinkWithTracking>
      </SnackDetails>
    </SnackInfoCard>
  );
};
